import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TREATMENTS = [
  'Body Contouring',
  'Breast Surgery',
  'Non-Surgical Aesthetics',
  'Skin Cancer Treatment & Reconstruction',
  'Reconstructive Surgery',
  'Hand Surgery',
  'Scar Management',
  'Minor Operations',
  'Laser Treatments',
  'General Enquiry'
];

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
};

function ContactSidebar() {
  return (
    <motion.div
      initial={{ opacity: 0, x: -30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      className="lg:col-span-1"
    >
      <div className="sticky top-32">
        <h2 className="text-heading-3 text-clinic-charcoal mb-8">Contact Details</h2>
        <div className="space-y-6 mb-12">
          <a href="mailto:contact@kavishmaheshwari.com" className="flex items-start gap-4 group" data-testid="contact-email-link">
            <div className="w-12 h-12 flex items-center justify-center border border-clinic-border group-hover:bg-clinic-charcoal group-hover:border-clinic-charcoal transition-colors">
              <Mail size={18} className="text-clinic-charcoal group-hover:text-clinic-bg transition-colors" />
            </div>
            <div>
              <p className="text-caption mb-1">Email</p>
              <p className="font-body text-clinic-charcoal group-hover:text-clinic-stone transition-colors">contact@kavishmaheshwari.com</p>
            </div>
          </a>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center border border-clinic-border">
              <Phone size={18} className="text-clinic-charcoal" />
            </div>
            <div>
              <p className="text-caption mb-1">Phone</p>
              <p className="font-body text-clinic-charcoal">Available on request</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center border border-clinic-border">
              <MapPin size={18} className="text-clinic-charcoal" />
            </div>
            <div>
              <p className="text-caption mb-1">Locations</p>
              <div className="space-y-2 font-body text-clinic-charcoal text-sm">
                <p className="font-medium">The Private Clinic</p>
                <p className="text-clinic-stone">Harley Street, London</p>
                <p className="text-clinic-stone">Northampton</p>
                <p className="text-clinic-stone">Birmingham</p>
                <p className="font-medium mt-3">Tonic Weight Loss Surgery</p>
                <p className="text-clinic-stone">Ashby</p>
                <p className="text-clinic-stone">Luton</p>
                <p className="font-medium mt-3">Hale Medical Centre</p>
                <p className="text-clinic-stone">Manchester</p>
                <p className="font-medium mt-3">3 Counties Clinic</p>
                <p className="text-clinic-stone">Bedford</p>
              </div>
            </div>
          </div>
        </div>
        <div className="p-6 bg-clinic-muted border border-clinic-border">
          <h3 className="font-heading text-lg text-clinic-charcoal mb-3">Response Time</h3>
          <p className="text-body-lg text-sm">
            We aim to respond to all enquiries within 48 hours during business days. For urgent medical concerns, please contact your GP or NHS 111.
          </p>
        </div>
      </div>
    </motion.div>
  );
}

function ContactForm({ formData, handleChange, handleSubmit, isSubmitting, isSubmitted, setIsSubmitted }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, delay: 0.3 }}
      className="lg:col-span-2"
    >
      {isSubmitted ? (
        <div className="text-center py-20 px-8 bg-white border border-clinic-border" data-testid="form-success-message">
          <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-clinic-charcoal">
            <CheckCircle size={32} className="text-clinic-bg" />
          </div>
          <h2 className="text-heading-2 text-clinic-charcoal mb-4">Thank you for your enquiry</h2>
          <p className="text-body-lg max-w-md mx-auto mb-8">
            We have received your message and will be in touch within 48 hours.
          </p>
          <button onClick={() => setIsSubmitted(false)} className="btn-secondary" data-testid="submit-another-btn">
            Submit Another Enquiry
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white border border-clinic-border p-8 md:p-12" data-testid="contact-form">
          <h2 className="text-heading-3 text-clinic-charcoal mb-8">Request a Consultation</h2>
          <div className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-caption mb-2">Full Name *</label>
              <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="input-flat" placeholder="Your full name" data-testid="input-name" />
            </div>
            <div>
              <label htmlFor="email" className="block text-caption mb-2">Email Address *</label>
              <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="input-flat" placeholder="your@email.com" data-testid="input-email" />
            </div>
            <div>
              <label htmlFor="phone" className="block text-caption mb-2">Phone Number (Optional)</label>
              <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="input-flat" placeholder="+44 7XXX XXXXXX" data-testid="input-phone" />
            </div>
            <div>
              <label htmlFor="treatment_interest" className="block text-caption mb-2">Treatment of Interest</label>
              <select id="treatment_interest" name="treatment_interest" value={formData.treatment_interest} onChange={handleChange} className="input-flat bg-transparent cursor-pointer" data-testid="select-treatment">
                <option value="">Select a treatment area</option>
                {TREATMENTS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="message" className="block text-caption mb-2">Your Message *</label>
              <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} className="input-flat resize-none" placeholder="Please share any details about your enquiry, concerns, or questions you'd like addressed during a consultation." data-testid="textarea-message" />
            </div>
            <p className="text-sm text-clinic-stone">
              By submitting this form, you agree that your information will be handled in accordance with our privacy policy. Your data is treated with complete confidentiality.
            </p>
            <button type="submit" disabled={isSubmitting} className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="submit-form-btn">
              {isSubmitting ? 'Submitting...' : <><Send size={16} /> Send Enquiry</>}
            </button>
          </div>
        </form>
      )}
    </motion.div>
  );
}

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', treatment_interest: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success('Enquiry submitted successfully');
        setFormData({ name: '', email: '', phone: '', treatment_interest: '', message: '' });
      }
    } catch {
      toast.error('Failed to submit enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-clinic-bg" data-testid="contact-page">
      <section className="pt-32 pb-20 md:pt-40 md:pb-28" data-testid="contact-hero">
        <div className="container-clinic">
          <motion.div initial="initial" animate="animate" variants={{ animate: { transition: { staggerChildren: 0.1 } } }} className="max-w-3xl">
            <motion.p variants={fadeInUp} className="text-caption mb-4">Contact</motion.p>
            <motion.h1 variants={fadeInUp} className="text-heading-1 text-clinic-charcoal mb-8">Get in touch</motion.h1>
            <motion.p variants={fadeInUp} className="text-body-lg">
              Request a consultation or ask a question. We aim to respond to all enquiries within 48 hours. Your information is treated with complete confidentiality.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="pb-20 md:pb-32" data-testid="contact-form-section">
        <div className="container-clinic">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-20">
            <ContactSidebar />
            <ContactForm formData={formData} handleChange={handleChange} handleSubmit={handleSubmit} isSubmitting={isSubmitting} isSubmitted={isSubmitted} setIsSubmitted={setIsSubmitted} />
          </div>
        </div>
      </section>

      <section className="section-padding bg-clinic-muted" data-testid="additional-info-section">
        <div className="container-clinic">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: 'Confidentiality', description: 'All enquiries and consultations are treated with the utmost discretion and confidentiality.' },
              { title: 'No Obligation', description: "Requesting a consultation does not commit you to any procedure. It's simply a conversation." },
              { title: 'Professional Care', description: 'Every enquiry is personally reviewed to ensure you receive appropriate guidance.' }
            ].map((item, index) => (
              <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }} className="text-center p-8" data-testid={`info-item-${index}`}>
                <h3 className="font-heading text-xl text-clinic-charcoal mb-3">{item.title}</h3>
                <p className="text-body-lg">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
